/** Shared A5 print / PDF helpers used by application and letter documents. */

export const A5_PAGE_CSS = `
  @page { size: A5 portrait; margin: 8mm; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Georgia", "Times New Roman", serif;
    color: #111;
    font-size: 9.2pt;
    line-height: 1.35;
  }
  h1 { font-size: 13pt; margin: 0 0 2mm; text-align: center; }
  h2 { font-size: 10pt; margin: 4mm 0 1.5mm; border-bottom: 1px solid #333; padding-bottom: 1mm; }
  .muted { color: #555; font-size: 8pt; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 2mm; }
  th, td { border: 1px solid #333; padding: 1.2mm 2mm; text-align: left; vertical-align: top; }
  th { background: #f1eae7; font-size: 8pt; text-transform: uppercase; letter-spacing: .3px; }
  td.num, th.num { text-align: right; }
  .head { text-align: center; margin-bottom: 3mm; }
  .head .org { font-size: 12.5pt; font-weight: bold; }
  .sign { margin-top: 8mm; display: flex; justify-content: space-between; font-size: 8.5pt; }
`;

/** Renders standalone HTML in a hidden iframe and opens the browser print / save-as-PDF dialog. */
export function printHtmlDocument(title: string, bodyHtml: string, css = A5_PAGE_CSS) {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc) {return;}
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title><style>${css}</style></head><body>${bodyHtml}</body></html>`,
  );
  doc.close();

  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 1500);
  };
  if (doc.readyState === "complete") {window.setTimeout(run, 120);}
  else {frame.onload = () => window.setTimeout(run, 120);}
}

export function escapeHtml(v: unknown) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const row = (label: string, value: unknown) =>
  `<tr><th style="width:38%">${escapeHtml(label)}</th><td>${escapeHtml(value || "—")}</td></tr>`;

const money = (n: unknown) =>
  new Intl.NumberFormat("en-IN").format(Math.round(Number(n) || 0));

export type PrintableApplication = {
  ref_no: string;
  application_date: string;
  status: string;
  applicant_name: string;
  applicant_address?: string | null;
  applicant_mobile?: string | null;
  ration_card_no?: string | null;
  beneficiary_name?: string | null;
  beneficiary_relation?: string | null;
  categoryName?: string | null | undefined;
  subcategory?: string | null;
  purpose?: string | null;
  total_requirement: number | string;
  applicant_contribution: number | string;
  our_contribution: number | string;
  other_received: number | string;
  balance_required: number | string | null;
  bank_account_holder?: string | null;
  bank_name?: string | null;
  bank_branch?: string | null;
  bank_account_no?: string | null;
  bank_ifsc?: string | null;
  remarks?: string | null;
};

/** A5-sized application summary sheet. */
export function applicationDocHtml(
  app: PrintableApplication,
  settings: Record<string, string> = {},
  orgs: { name: string; amount?: number }[] = [],
) {
  return `
  <div class="head">
    <div class="org">${escapeHtml(settings["org_heading"] || "Sadharmik Assistance Desk")}</div>
    <div class="muted">${escapeHtml(settings["office_address"] || "")}</div>
    <div class="muted">Mobile: ${escapeHtml(settings["mobile"] || "")} &nbsp; Email: ${escapeHtml(settings["email"] || "")}</div>
  </div>
  <h1>Financial Assistance Application</h1>
  <p class="muted" style="text-align:center;margin:0 0 3mm">
    Ref. No. <strong>${escapeHtml(app.ref_no)}</strong> &nbsp;·&nbsp; Date ${escapeHtml(app.application_date)} &nbsp;·&nbsp; Status ${escapeHtml(app.status)}
  </p>

  <h2>Applicant &amp; Beneficiary</h2>
  <table>
    ${row("Applicant Name", app.applicant_name)}
    ${row("Address", app.applicant_address)}
    ${row("Mobile", app.applicant_mobile)}
    ${row("Ration Card No.", app.ration_card_no)}
    ${row("Beneficiary", app.beneficiary_name || app.applicant_name)}
    ${row("Relation", app.beneficiary_relation)}
  </table>

  <h2>Assistance Details</h2>
  <table>
    ${row("Category", app.categoryName)}
    ${row("Subcategory", app.subcategory)}
    ${row("Purpose", app.purpose)}
  </table>

  <h2>Financial Summary (Rs.)</h2>
  <table>
    <tr><th>A. Total Requirement</th><td class="num">${money(app.total_requirement)}</td></tr>
    <tr><th>B. Own Contribution</th><td class="num">${money(app.applicant_contribution)}</td></tr>
    <tr><th>C. Our Contribution</th><td class="num">${money(app.our_contribution)}</td></tr>
    <tr><th>D. Other Received</th><td class="num">${money(app.other_received)}</td></tr>
    <tr><th>E. Balance Required (A-B-C-D)</th><td class="num"><strong>${money(app.balance_required)}</strong></td></tr>
  </table>

  <h2>Bank Details</h2>
  <table>
    ${row("Account Holder", app.bank_account_holder)}
    ${row("Bank / Branch", [app.bank_name, app.bank_branch].filter(Boolean).join(" — "))}
    ${row("Account No.", app.bank_account_no)}
    ${row("IFSC", app.bank_ifsc)}
  </table>

  ${
    orgs.length
      ? `<h2>Recommended Institutions</h2>
    <table>
      <thead><tr><th>Institution</th><th class="num">Amount Requested</th></tr></thead>
      <tbody>${orgs
        .map(
          (o) =>
            `<tr><td>${escapeHtml(o.name)}</td><td class="num">${o.amount ? money(o.amount) : "—"}</td></tr>`,
        )
        .join("")}</tbody>
    </table>`
      : ""
  }

  ${app.remarks ? `<h2>Remarks</h2><p>${escapeHtml(app.remarks)}</p>` : ""}

  <div class="sign">
    <span>Prepared by: ______________</span>
    <span>For ${escapeHtml(settings["org_heading"] || "the Trust")}</span>
  </div>`;
}

/** Opens WhatsApp with a prepared message (manual send by the user). */
export function shareOnWhatsApp(phone: string | null | undefined, message: string) {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const to = digits ? (digits.length === 10 ? `91${digits}` : digits) : "";
  const url = to
    ? `https://wa.me/${to}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank", "noopener");
}

/** Opens the default mail client with a prepared email. */
export function shareByEmail(to: string | null | undefined, subject: string, body: string) {
  window.location.href = `mailto:${to ?? ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Clones a live DOM element (with the app's stylesheets) into a hidden iframe and
 * opens the browser print / Save-as-PDF dialog. Used to download all letters at once.
 */
export function printElementAsPdf(title: string, el: HTMLElement) {
  const headStyles = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]'),
  )
    .map((n) => n.outerHTML)
    .join("\n");

  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc) {return;}
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title>${headStyles}<style>@page { size: A5 portrait; margin: 6mm; } html,body{margin:0;padding:0;background:#fff;} .no-print{display:none !important;}</style></head><body>${el.innerHTML}</body></html>`,
  );
  doc.close();

  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 2000);
  };
  if (doc.readyState === "complete") {window.setTimeout(run, 400);}
  else {frame.onload = () => window.setTimeout(run, 400);}
}
